import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ScanLine, RefreshCw, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TONE = {
  pass: { c: '#34d399', label: 'PASS' },
  needs_adaptation: { c: '#fbbf24', label: 'ADAPT' },
  conflicting: { c: '#fb7185', label: 'CONFLICT' },
  irrelevant: { c: '#818cf8', label: 'N/A' },
};

/** Every screen, judged against every archetype we serve. Read-only: it reports, it doesn't edit. */
export const ExperienceAuditPanel = () => {
  const { getAuthHeader } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [who, setWho] = useState('high_school');

  const load = useCallback((refresh = false) => {
    setBusy(true);
    axios.get(`${API}/admin/experience-scan${refresh ? '?refresh=true' : ''}`, getAuthHeader())
      .then(r => setD(r.data))
      .catch(() => toast.error('Could not run the scan'))
      .finally(() => setBusy(false));
  }, [getAuthHeader]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <p className="text-sm text-slate-300" data-testid="audit-loading">Scanning…</p>;
  const keys = Object.keys(d.archetypes);
  const rows = d.modules.filter(m => m.verdicts && Object.keys(m.verdicts).length);
  const sorted = [...rows].sort((a, b) =>
    b.verdicts[who].count - a.verdicts[who].count || a.module.localeCompare(b.module));

  return (
    <div className="space-y-6" data-testid="experience-audit-panel">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-300/80">Admin · experience audit</p>
          <h1 className="font-outfit text-3xl md:text-4xl font-semibold text-white mt-2">
            Does every screen know who it's talking to?
          </h1>
          <p className="text-sm text-slate-300 mt-2 max-w-3xl">{d.how_it_works}</p>
          <p className="text-xs text-slate-300 mt-2">
            {d.totals.modules_scanned} modules · {d.totals.strings_checked.toLocaleString()} lines of
            copy and questions checked · clean for everyone: {d.totals.clean_for_everyone} ·
            last run {String(d.created_at).slice(0, 16).replace('T', ' ')}
          </p>
        </div>
        <Button onClick={() => load(true)} disabled={busy} data-testid="rerun-scan"
          className="ml-auto rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? 'animate-spin' : ''}`} /> Run the scan again
        </Button>
      </div>

      {/* Who is worst served */}
      <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="audit-archetypes">
        {keys.map(k => {
          const a = d.archetypes[k];
          const on = k === who;
          return (
            <button key={k} onClick={() => setWho(k)} data-testid={`audit-archetype-${k}`}
              className={`text-left rounded-2xl border p-4 transition-colors ${on
                ? 'border-cyan-400/50 bg-cyan-500/[0.07]' : 'border-white/10 bg-slate-900/60 hover:border-white/25'}`}>
              <p className="text-sm text-white font-medium">{a.label}</p>
              <p className="text-xs text-slate-300">age {a.age}</p>
              <p className="text-2xl font-semibold mt-2 fp-num"
                style={{ color: a.findings ? (a.conflicting ? '#fb7185' : '#fbbf24') : '#34d399' }}>
                {a.findings}
              </p>
              <p className="text-xs text-slate-300">
                {a.conflicting} conflicting · {a.needs_adaptation} to reword
              </p>
            </button>
          );
        })}
      </div>

      {/* Module by module for the selected person */}
      <div className="rounded-2xl border border-white/10 bg-slate-900/60 p-5" data-testid="audit-modules">
        <div className="flex items-center gap-2">
          <ScanLine className="w-4 h-4 text-cyan-300" />
          <h2 className="font-outfit text-xl font-semibold text-white">
            As a {d.archetypes[who].label.toLowerCase()}
          </h2>
        </div>
        <div className="mt-4 space-y-1.5">
          {sorted.map(m => {
            const v = m.verdicts[who];
            const t = TONE[v.verdict];
            const isOpen = open === m.module;
            return (
              <div key={m.module} className="rounded-xl bg-slate-800/40 overflow-hidden">
                <button onClick={() => setOpen(isOpen ? null : m.module)}
                  data-testid={`audit-module-${m.module.replace(/\W+/g, '-').toLowerCase()}`}
                  className="w-full flex flex-wrap items-center gap-3 px-4 py-2.5 text-left hover:bg-white/[0.03] transition-colors">
                  <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded"
                    style={{ background: `${t.c}22`, color: t.c }}>{t.label}</span>
                  <span className="text-sm text-white">{m.module}</span>
                  <span className="text-xs text-slate-300">{m.strings_checked} checked</span>
                  {m.adapted_count > 0 && (
                    <span className="text-xs text-emerald-300/80" title="Already switched on the user's context, or living in a per-archetype block">
                      {m.adapted_count} adapted
                    </span>
                  )}
                  {v.count > 0 && (
                    <span className="text-xs text-amber-300">
                      {v.count} {v.count === 1 ? 'issue' : 'issues'}
                      {v.input_count ? ` · ${v.input_count} we actually ask` : ''}
                    </span>
                  )}
                  {v.count > 0 && (
                    <ChevronDown className={`w-3.5 h-3.5 ml-auto text-slate-300 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  )}
                </button>
                {isOpen && v.findings.length > 0 && (
                  <div className="px-4 pb-3 space-y-2">
                    {v.findings.map((f, i) => (
                      <div key={i} className="rounded-lg bg-[#0a1729]/50 p-3">
                        <p className="text-xs" style={{ color: TONE[f.severity].c }}>
                          {f.assumption} · {f.kind === 'input' ? 'a question we ask' : 'wording'} ·
                          {m.file}:{f.line}
                        </p>
                        <p className="text-sm text-slate-200 mt-1.5">"{f.text}"</p>
                        {f.suggestion && (
                          <p className="text-xs text-emerald-300/90 mt-1.5">
                            say instead: {f.suggestion}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ExperienceAuditPanel;
